/**
 * EditSongInfoDialog — edit the loaded beatmap's metadata (song name, artist,
 * beatmapper, BPM, offset), mirroring the Flow editor's "Edit Map Info" dialog.
 * Opened by clicking the song card in the bottom playback bar. Changes update
 * the in-memory beatmap (persisted when the map is saved/exported).
 *
 * Note: cover-art replacement, audio replacement and online cover search are
 * NOT wired here — the SR API client exposes no metadata/artwork/audio update
 * endpoints, so those Flow features need backend support before they can ship.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { getArtworkUrl } from '../../api/client';
import { color, font, ui, z } from '../ui/theme';
import { IcClose, IcMusic } from '../ui/icons';

export default function EditSongInfoDialog() {
  const open = useEditorStore(s => s.editInfoOpen);
  const setOpen = useEditorStore(s => s.setEditInfoOpen);
  const beatmap = useEditorStore(s => s.beatmap);
  const updateMeta = useEditorStore(s => s.updateMeta);

  const [name, setName] = useState('');
  const [author, setAuthor] = useState('');
  const [beatmapper, setBeatmapper] = useState('');
  const [bpm, setBpm] = useState('120');
  const [offsetMs, setOffsetMs] = useState('0');
  const [coverFailed, setCoverFailed] = useState(false);

  // Re-seed the fields from the beatmap each time the dialog opens.
  useEffect(() => {
    if (!open || !beatmap) return;
    setCoverFailed(false);
    setName(beatmap.name ?? '');
    setAuthor(beatmap.author ?? '');
    setBeatmapper(beatmap.beatmapper ?? '');
    setBpm(String(beatmap.bpm ?? 120));
    setOffsetMs(String(Math.round(beatmap.offset ?? 0))); // offset is stored in ms
  }, [open, beatmap]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  const closeRef = useRef<HTMLButtonElement>(null);

  if (!open || !beatmap) return null;

  const save = () => {
    const bpmNum = parseFloat(bpm);
    const offNum = parseFloat(offsetMs);
    updateMeta({
      name: name.trim() || '(untitled)',
      author: author.trim(),
      beatmapper: beatmapper.trim(),
      ...(Number.isFinite(bpmNum) && bpmNum > 0 ? { bpm: Math.max(40, Math.min(500, bpmNum)) } : null),
      ...(Number.isFinite(offNum) ? { offset: Math.round(offNum) } : null),
    });
    setOpen(false);
  };

  // Try the artwork URL whenever there's an id; has_artwork is unreliable for
  // not-yet-hydrated maps, so we fall back to the music glyph on load error.
  const coverUrl = beatmap.id && !coverFailed ? getArtworkUrl(beatmap.id) : null;

  return (
    <div style={backdropStyle} onMouseDown={() => setOpen(false)}>
      <div style={cardStyle} onMouseDown={e => e.stopPropagation()}>
        {/* Title bar */}
        <div style={titleBarStyle}>
          <div style={titleStyle}>Edit Song Info</div>
          <button
            ref={closeRef}
            aria-label="Close"
            onClick={() => setOpen(false)}
            style={closeBtnStyle}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent)'; e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'oklch(0.14 calc(0.022 * var(--tint-c)) var(--tint-h))'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.55)'; e.currentTarget.style.color = 'var(--accent)'; }}
          ><IcClose size={15} /></button>
        </div>

        {/* Body */}
        <div style={bodyStyle}>
          {/* Cover thumbnail (read-only) */}
          <div style={coverRowStyle}>
            <div style={coverThumbStyle}>
              {coverUrl
                ? <img src={coverUrl} alt="cover" onError={() => setCoverFailed(true)} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }} />
                : <IcMusic size={26} />}
            </div>
            <div style={{ fontSize: 10, color: color.inkFaint, lineHeight: 1.5 }}>
              Cover &amp; audio replacement aren&apos;t available yet (needs backend support).
            </div>
          </div>

          <Field label="Song Name" value={name} onChange={setName} placeholder="e.g. Dramophone" />
          <Field label="Artist" value={author} onChange={setAuthor} placeholder="e.g. CARAVAN Palace" />
          <Field label="Beatmapper" value={beatmapper} onChange={setBeatmapper} placeholder="Your name" />
          <div style={{ display: 'flex', gap: 10 }}>
            <Field label="BPM" value={bpm} onChange={setBpm} placeholder="120" numeric />
            <Field label="Offset (ms)" value={offsetMs} onChange={setOffsetMs} placeholder="0" numeric />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button style={cancelBtnStyle} onClick={() => setOpen(false)}>Cancel</button>
            <button style={saveBtnStyle} onClick={save}>Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, numeric }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; numeric?: boolean;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
      <span style={fieldLabelStyle}>{label}</span>
      <input
        value={value}
        inputMode={numeric ? 'decimal' : undefined}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        style={inputStyle}
      />
    </label>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────

const backdropStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: z.modal,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'oklch(0 0 0 / 0.55)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
};

const cardStyle: React.CSSProperties = {
  ...ui.dialog,
  width: 420, maxWidth: '92vw', maxHeight: 'calc(100vh - 24px)',
  display: 'flex', flexDirection: 'column', overflowY: 'auto',
};

const titleBarStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  height: 44, padding: '0 12px 0 16px',
  borderBottom: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.14)',
};

const titleStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 700, color: color.accent,
  fontFamily: font.sans, letterSpacing: 1.2, textTransform: 'uppercase',
};

const closeBtnStyle: React.CSSProperties = {
  width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
  borderRadius: '50%', background: 'transparent',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.55)',
  color: 'var(--accent)', cursor: 'pointer', flexShrink: 0,
  transition: 'background 0.15s ease, color 0.15s ease, border-color 0.15s ease',
};

const bodyStyle: React.CSSProperties = {
  padding: 18, display: 'flex', flexDirection: 'column', gap: 12,
};

const coverRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12,
};

const coverThumbStyle: React.CSSProperties = {
  width: 56, height: 56, borderRadius: 10, flexShrink: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.5)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
  color: color.inkFaint,
};

const fieldLabelStyle: React.CSSProperties = {
  fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
  color: color.accentSoft, fontFamily: font.sans,
};

const inputStyle: React.CSSProperties = {
  ...ui.input,
  padding: '8px 10px', fontSize: 12,
};

const cancelBtnStyle: React.CSSProperties = {
  padding: '8px 16px', borderRadius: 999,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
  color: color.inkDim, fontFamily: font.sans, fontSize: 11, fontWeight: 600, cursor: 'pointer',
};

const saveBtnStyle: React.CSSProperties = {
  padding: '8px 18px', borderRadius: 999,
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)',
  background: 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.3), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.18))',
  color: 'var(--accent)', fontFamily: font.sans, fontSize: 11, fontWeight: 700, cursor: 'pointer',
};
